import { useState } from "react";
import { Link } from "react-router-dom";
import { X, MapPin, Clock } from "lucide-react";
import {
  MONTH_NAMES,
  DAY_NAMES_SHORT,
  getMonthCells,
  toISO,
  formatFullDate,
} from "@/lib/calendar";
import { CATEGORY_BADGE } from "@/lib/categoryColors";
import { formatTimeRange, formatDateRangeShort } from "@/lib/dateFormat";

const CATEGORY_DOT = {
  Concert: "bg-[#E55934]",
  Festival: "bg-[#DC2626]",
  Sport: "bg-[#2563EB]",
  Marché: "bg-[#16A34A]",
  Repas: "bg-[#7C3AED]",
  Soirée: "bg-neutral-400",
};

function DayCell({ day, iso, events, isToday, isPast, selected, onSelect }) {
  const categories = [...new Set(events.map((e) => e.category))];
  const shownCats = categories.slice(0, 4);
  const extra = categories.length - shownCats.length;
  const hasEvents = events.length > 0;

  return (
    <button
      type="button"
      disabled={!hasEvents}
      onClick={() => hasEvents && onSelect(iso)}
      className={`relative aspect-square sm:aspect-[5/4] flex flex-col items-center justify-start gap-1 p-1.5 border border-[#FDE68A] text-xs transition-all
        ${hasEvents ? "cursor-pointer hover:bg-[#FEF3C7]" : "cursor-default"}
        ${selected ? "bg-[#FDE68A] border-[#EAB308] ring-2 ring-[#EAB308]/40 z-10" : "bg-[#FEFCE8]"}
        ${isPast ? "opacity-40" : ""}
      `}
      data-testid={`day-${iso}`}
    >
      <span
        className={`font-heading font-semibold text-sm sm:text-base ${
          isToday
            ? "bg-[#EAB308] text-white w-6 h-6 rounded-full flex items-center justify-center"
            : "text-ink"
        }`}
      >
        {day}
      </span>
      {hasEvents && (
        <div className="flex flex-wrap gap-0.5 justify-center mt-auto">
          {shownCats.map((c) => (
            <span
              key={c}
              className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${CATEGORY_DOT[c] || "bg-neutral-400"}`}
              title={c}
            />
          ))}
          {extra > 0 && (
            <span className="text-[9px] font-semibold text-muted_text">+{extra}</span>
          )}
        </div>
      )}
    </button>
  );
}

function DayPanel({ year, month, dayIso, events, onClose }) {
  const day = parseInt(dayIso.split("-")[2], 10);
  return (
    <div
      className="mt-4 bg-white border-2 border-[#EAB308] rounded-xl overflow-hidden shadow-lg fade-in"
      data-testid="day-panel"
    >
      <div className="flex items-center justify-between p-4 sm:p-5 bg-[#FEF3C7] border-b border-[#FDE68A]">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted_text font-semibold">
            {events.length} événement{events.length > 1 ? "s" : ""}
          </div>
          <h3 className="font-heading font-bold text-lg sm:text-xl text-ink capitalize">
            {formatFullDate(year, month, day)}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/60 text-muted_text"
          aria-label="Fermer"
          data-testid="close-day-panel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {events.map((e) => (
          <Link
            key={e.id}
            to={`/events/${e.id}`}
            className="flex gap-3 p-3 rounded-xl border border-[#FDE68A] bg-[#FEFCE8] hover:bg-white hover:border-[#EAB308] transition-all"
            data-testid="day-event-card"
          >
            <img
              src={e.image_url}
              alt={e.title}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover shrink-0 bg-[#FEF3C7]"
              onError={(ev) => {
                ev.currentTarget.src =
                  "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=200&q=80";
              }}
            />
            <div className="min-w-0 flex-1">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  CATEGORY_BADGE[e.category] || "bg-white text-ink"
                }`}
              >
                {e.category}
              </span>
              <h4 className="font-heading font-semibold text-sm text-ink mt-1 line-clamp-2">
                {e.title}
              </h4>
              <div className="text-xs text-muted_text mt-1 flex flex-wrap gap-x-2">
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatTimeRange(e.time, e.end_time)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {e.commune}
                </span>
                {e.end_date && e.end_date !== e.date && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-[#FEF3C7] rounded-full text-[#854D0E] font-semibold">
                    {formatDateRangeShort(e.date, e.end_date)}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function MonthGrid({ year, month, eventsByDay, todayIso }) {
  const [selected, setSelected] = useState(null);
  const cells = getMonthCells(year, month);

  return (
    <div className="bg-white rounded-2xl border border-[#FDE68A] p-4 sm:p-6 shadow-sm" data-testid={`month-${year}-${String(month + 1).padStart(2, "0")}`}>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-heading font-bold text-2xl sm:text-3xl text-[#CA8A04] capitalize">
          {MONTH_NAMES[month]}
        </h3>
        <span className="font-heading font-bold text-xl sm:text-2xl text-[#EAB308]/60">
          {year}
        </span>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAY_NAMES_SHORT.map((d) => (
          <div
            key={d}
            className="text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted_text py-1.5"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-0">
        {cells.map((day, i) => {
          if (!day) return <div key={i} className="aspect-square sm:aspect-[5/4] bg-transparent" />;
          const iso = toISO(new Date(year, month, day));
          const events = eventsByDay.get(iso) || [];
          const isToday = iso === todayIso;
          const isPast = iso < todayIso;
          return (
            <DayCell
              key={i}
              day={day}
              iso={iso}
              events={events}
              isToday={isToday}
              isPast={isPast}
              selected={selected === iso}
              onSelect={setSelected}
            />
          );
        })}
      </div>

      {selected && (
        <DayPanel
          year={year}
          month={month}
          dayIso={selected}
          events={eventsByDay.get(selected) || []}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
